import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { useCompanies, useCreateUser, useUpdateUser, useGroups } from '@/hooks/useApi';
import { toast } from '@/components/ui/Toast';
const EMPTY = {
    username: '', name: '', phone: '', email: '', password: '',
    companyId: undefined,
    groupId: undefined,
    role: 'USER',
    birthday: '',
};
export function UserFormModal({ open, onClose, user, myRole }) {
    const { t } = useTranslation();
    const create = useCreateUser();
    const update = useUpdateUser();
    const companiesQ = useCompanies();
    const [form, setForm] = useState(EMPTY);
    useEffect(() => {
        if (user) {
            setForm({
                username: user.username,
                name: user.name,
                phone: user.phone ?? '',
                email: user.email ?? '',
                password: '',
                companyId: user.companyId ?? undefined,
                groupId: user.groupId ?? undefined,
                role: user.role,
                birthday: '',
            });
        }
        else {
            setForm(EMPTY);
        }
    }, [user, open]);
    // Groups for the currently-selected company; cleared when company changes.
    const groupsQ = useGroups(form.companyId);
    const roleOptions = myRole === 'SUPER_USER'
        ? [['USER', t('roles.USER')]]
        : myRole === 'SUPER_ADMIN'
            ? [['USER', 'USER'], ['SUPER_USER', 'SUPER_USER'], ['ADMIN', 'ADMIN'], ['SUPER_ADMIN', 'SUPER_ADMIN']]
            : [['USER', 'USER'], ['SUPER_USER', 'SUPER_USER'], ['ADMIN', 'ADMIN']];
    const submit = async () => {
        if (!form.username.trim() || !form.name.trim()) {
            toast.error('نام و نام کاربری الزامی است');
            return;
        }
        if ((form.role === 'USER' || form.role === 'SUPER_USER') && !form.companyId) {
            toast.error('شرکت را انتخاب کنید');
            return;
        }
        const payload = {
            username: form.username, name: form.name,
            phone: form.phone || null, email: form.email || null,
            companyId: form.companyId ?? null,
            groupId: form.groupId ?? null,
            role: form.role,
            birthday: form.birthday || null,
        };
        if (form.password)
            payload.password = form.password;
        try {
            if (user)
                await update.mutateAsync({ id: user.id, ...payload });
            else
                await create.mutateAsync(payload);
            toast.success(t('common.save'));
            onClose();
        }
        catch (e) {
            toast.error(e?.response?.data?.error || 'خطا');
        }
    };
    return (_jsx(Modal, { open: open, onClose: onClose, title: user ? t('user.editUser') : t('user.newUser'), size: "lg", footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", onClick: onClose, children: t('common.cancel') }), _jsx(Button, { onClick: submit, loading: create.isPending || update.isPending, children: t('common.save') })] }), children: _jsxs("div", { className: "grid gap-3 sm:grid-cols-2", children: [_jsx(Field, { label: t('common.username'), children: _jsx(Input, { value: form.username, onChange: (e) => setForm({ ...form, username: e.target.value }), disabled: !!user }) }), _jsx(Field, { label: t('common.name'), children: _jsx(Input, { value: form.name, onChange: (e) => setForm({ ...form, name: e.target.value }) }) }), _jsx(Field, { label: t('common.phone'), children: _jsx(Input, { value: form.phone, onChange: (e) => setForm({ ...form, phone: e.target.value }), dir: "ltr" }) }), _jsx(Field, { label: t('common.email'), children: _jsx(Input, { type: "email", value: form.email, onChange: (e) => setForm({ ...form, email: e.target.value }), dir: "ltr" }) }), _jsx(Field, { label: t('common.role'), children: _jsx(Select, { value: form.role, onChange: (e) => setForm({ ...form, role: e.target.value }), children: roleOptions.map(([v, label]) => _jsx("option", { value: v, children: label }, v)) }) }), _jsx(Field, { label: t('common.company'), children: _jsxs(Select, { value: form.companyId ?? '', onChange: (e) => setForm({
                            ...form,
                            companyId: e.target.value ? Number(e.target.value) : undefined,
                            // Reset group when company changes — groups belong to a company.
                            groupId: undefined,
                        }), children: [_jsx("option", { value: "", children: "\u2014" }), companiesQ.data?.map((c) => _jsx("option", { value: c.id, children: c.name }, c.id))] }) }), _jsx(Field, { label: "\u06AF\u0631\u0648\u0647", hint: !form.companyId ? 'ابتدا شرکت را انتخاب کنید' : undefined, children: _jsxs(Select, { value: form.groupId ?? '', disabled: !form.companyId, onChange: (e) => setForm({ ...form, groupId: e.target.value ? Number(e.target.value) : undefined }), children: [_jsx("option", { value: "", children: "\u2014" }), (groupsQ.data || []).map((g) => _jsx("option", { value: g.id, children: g.name }, g.id))] }) }), _jsx(Field, { label: t('user.birthday'), children: _jsx(Input, { type: "date", value: form.birthday, onChange: (e) => setForm({ ...form, birthday: e.target.value }), dir: "ltr" }) }), _jsx(Field, { label: t('auth.password'), hint: user ? 'برای تغییر نکردن، خالی بگذارید' : 'حداقل ۸ کاراکتر — برای ورود اول خالی بگذارید', children: _jsx(Input, { type: "password", value: form.password, onChange: (e) => setForm({ ...form, password: e.target.value }), placeholder: user ? '••••••••' : '' }) })] }) }));
}
